import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useNavigate, useParams } from 'react-router-dom';
import useAuthorize from '../hooks/useAuthorize';
import { UserRole } from '../types/types';
import { apiDelete, apiDownload, apiGet } from './api';

const rowSummary = (row) => {
    const data = row.normalized_row_json || {};
    const party = data.importerName || data.exporterName || '';
    const counterparty = data.supplierName || data.buyerName || '';
    return {
        party,
        counterparty,
        hsn: data.hsnCode,
        date: data.transactionDate,
        value: data.totalValueSource,
        port: data.indianPortName,
        country: data.countryName,
    };
};

const ImportBatchDetail = () => {
    useAuthorize([UserRole.Admin, UserRole.ProductAdmin]);
    const { id } = useParams();
    const navigate = useNavigate();
    const [batch, setBatch] = useState(null);
    const [rows, setRows] = useState([]);
    const [status, setStatus] = useState('');
    const [page, setPage] = useState(0);
    const [total, setTotal] = useState(0);
    const [deleting, setDeleting] = useState(false);
    const [error, setError] = useState('');
    const perPage = 50;

    const fetchBatch = async () => {
        const response = await apiGet(`/import/batches/${id}`);
        setBatch(response.json);
    };

    const fetchRows = async () => {
        const response = await apiGet(`/import/batches/${id}/rows`, {
            _start: page * perPage,
            _end: page * perPage + perPage,
            status,
        });
        setRows(response.json || []);
        setTotal(response.total);
    };

    const fetchAll = async () => {
        setError('');
        try {
            await Promise.all([fetchBatch(), fetchRows()]);
        } catch (err) {
            setError(err.message);
        }
    };

    const handleDownload = async () => {
        if (!batch) return;
        setError('');
        try {
            await apiDownload(`/import/batches/${batch.id}/download`, batch.original_filename);
        } catch (err) {
            setError(err.message);
        }
    };

    const handleDelete = async () => {
        if (!batch) return;
        if (batch.status === 'processing') {
            setError('This batch is still processing. Refresh and try again after it finishes.');
            return;
        }
        const confirmed = window.confirm(
            `Delete "${batch.original_filename}" and all imported rows from this batch? This cannot be undone.`
        );
        if (!confirmed) return;

        setDeleting(true);
        setError('');
        try {
            const response = await apiDelete(`/import/batches/${batch.id}`);
            if (response.json?.sourceDeleteError) {
                setError(`Batch data was deleted, but the source file could not be deleted: ${response.json.sourceDeleteError}`);
                return;
            }
            navigate('/import');
        } catch (err) {
            setError(err.message);
        } finally {
            setDeleting(false);
        }
    };

    useEffect(() => {
        fetchAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, page, status]);

    return (
        <Box sx={{ p: 3 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} justifyContent="space-between" sx={{ mb: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => navigate('/import')}>
                        Dashboard
                    </Button>
                    <Typography variant="h6">Batch #{id}</Typography>
                </Stack>
                <Stack direction="row" spacing={1}>
                    <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={fetchAll}>
                        Refresh
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteIcon />}
                        disabled={!batch || batch.status === 'processing' || deleting}
                        onClick={handleDelete}
                    >
                        Delete
                    </Button>
                </Stack>
            </Stack>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {batch && (
                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
                        <Typography>File: {batch.original_filename}</Typography>
                        <Typography>Direction: {batch.trade_direction}</Typography>
                        <Typography>Status: {batch.status}</Typography>
                        <Typography>Rows: {batch.total_rows}</Typography>
                        <Typography>Inserted: {batch.inserted_rows}</Typography>
                        <Typography>Duplicate: {batch.duplicate_rows}</Typography>
                        <Typography>Failed: {batch.failed_rows}</Typography>
                        <Button size="small" startIcon={<DownloadIcon />} onClick={handleDownload} sx={{ width: 'fit-content' }}>
                            Download Source
                        </Button>
                    </Stack>
                    {batch.error_summary && <Alert severity="warning" sx={{ mt: 2 }}>{batch.error_summary}</Alert>}
                </Paper>
            )}

            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel>Status</InputLabel>
                        <Select
                            label="Status"
                            value={status}
                            onChange={(event) => {
                                setStatus(event.target.value);
                                setPage(0);
                            }}
                        >
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="inserted">Inserted</MenuItem>
                            <MenuItem value="duplicate">Duplicate</MenuItem>
                            <MenuItem value="failed">Failed</MenuItem>
                        </Select>
                    </FormControl>
                    <Typography variant="body2" color="text.secondary">
                        {total} row(s)
                    </Typography>
                </Stack>
            </Paper>

            <TableContainer component={Paper} variant="outlined">
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell>Row</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Party</TableCell>
                            <TableCell>Counterparty</TableCell>
                            <TableCell>HSN</TableCell>
                            <TableCell>Date</TableCell>
                            <TableCell>Port</TableCell>
                            <TableCell>Country</TableCell>
                            <TableCell align="right">USD</TableCell>
                            <TableCell>Error</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row) => {
                            const summary = rowSummary(row);
                            return (
                                <TableRow key={row.id}>
                                    <TableCell>{row.row_number}</TableCell>
                                    <TableCell>{row.status}</TableCell>
                                    <TableCell>{summary.party}</TableCell>
                                    <TableCell>{summary.counterparty}</TableCell>
                                    <TableCell>{summary.hsn}</TableCell>
                                    <TableCell>{summary.date}</TableCell>
                                    <TableCell>{summary.port}</TableCell>
                                    <TableCell>{summary.country}</TableCell>
                                    <TableCell align="right">{summary.value}</TableCell>
                                    <TableCell>{row.error_reason}</TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
                <Button disabled={page === 0} onClick={() => setPage(page - 1)}>Previous</Button>
                <Typography sx={{ alignSelf: 'center' }}>Page {page + 1}</Typography>
                <Button disabled={(page + 1) * perPage >= total} onClick={() => setPage(page + 1)}>Next</Button>
            </Stack>
        </Box>
    );
};

export default ImportBatchDetail;
